import React, { useEffect, useRef, useState } from 'react';
import { EditorInstance } from 'novel';
import { RectangleEllipsis, Bot, BotOff, PanelRightClose, PanelRightOpen, Link as LinkIcon, Bold, Italic, Underline, Strikethrough, Code, Quote, FileCode, List, ListOrdered, CheckSquare, Eye, Undo, Redo, Sparkles } from 'lucide-react';
import { PhotoIcon, TableCellsIcon } from '@heroicons/react/24/outline';
import { ToolbarButton } from './toolbar-button';
import { NodeSelector } from './node-selector';
import { isHeading, isParagraph, isParagraphEmpty, selectionContainsHeading } from '../utils/editor-state';
import { ImageInsertionDialog } from './image-insertion-dialog';
import { LinkDialog } from './link-dialog';
import { Tooltip as ReactTippyTooltip } from 'react-tippy';
import 'react-tippy/dist/tippy.css';

// Tooltip wrapper for TypeScript compatibility
const Tooltip = ({ children, ...props }: { children: React.ReactNode } & any) => {
  return <ReactTippyTooltip {...props}>{children}</ReactTippyTooltip>;
};

interface FixedToolbarProps {
  editor: EditorInstance | null;
  onAICommand: (type: 'generate' | 'refine' | 'summarize-title' | 'generate-image') => void;
  saveStatus: string;
  wordCount: number | null;
  isAgentMode?: boolean;
  onToggleAgentMode?: () => void;
  onInsertAsset?: () => void;
  onSave?: () => void;
  hasUnsavedChanges?: boolean;
  isAiProcessing?: boolean;
  isUploadingImage?: boolean;
  isReadOnly?: boolean;
  viewMode: 'visual' | 'markdown' | 'html';
  onViewModeChange: (mode: 'visual' | 'markdown' | 'html') => void;
}

export const FixedToolbar: React.FC<FixedToolbarProps> = ({
  editor,
  onAICommand,
  saveStatus,
  wordCount,
  isAgentMode,
  isAiProcessing = false,
  isUploadingImage = false,
  isReadOnly = false,
  onToggleAgentMode,
  onInsertAsset,
  onSave,
  hasUnsavedChanges,
  viewMode,
  onViewModeChange
}) => {
  const [isNodeSelectorOpen, setIsNodeSelectorOpen] = useState(false);
  const [isImageDialogOpen, setIsImageDialogOpen] = useState(false);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [linkData, setLinkData] = useState<{ url: string; text: string; isEditing: boolean }>({ url: '', text: '', isEditing: false });

  // Notify when link dialog opens/closes for refocus handling
  useEffect(() => {
    if (isLinkDialogOpen) {
      document.dispatchEvent(new CustomEvent('link-dialog-opened'));
    } else {
      document.dispatchEvent(new CustomEvent('link-dialog-closed'));
    }
  }, [isLinkDialogOpen]);
  // Refs and state for sticky toolbar behavior
  const toolbarRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<HTMLDivElement>(null);
  const editorObserverRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [isEditorVisible, setIsEditorVisible] = useState(true);
  const [toolbarHeight, setToolbarHeight] = useState(0);

  // Set up intersection observers to detect when toolbar should become sticky
  // and when editor is visible
  useEffect(() => {
    if (!toolbarRef.current || !observerRef.current) return;

    // Get initial measurements
    const toolbar = toolbarRef.current;
    setToolbarHeight(toolbar.offsetHeight);

    // Use a single state update function to prevent race conditions
    const updateStickyState = (isTopInView: boolean, isEditorInView: boolean) => {
      // Only show sticky toolbar when:
      // 1. Top sentinel is out of view (scrolled past the original toolbar position)
      // 2. AND editor is at least partially visible
      // 3. AND we're not already in the correct state (to prevent unnecessary renders)

      const shouldBeSticky = !isTopInView && isEditorInView;

      if (shouldBeSticky !== isSticky) {
        // Use RAF to ensure smooth transitions and prevent flashing
        requestAnimationFrame(() => {
          setIsSticky(shouldBeSticky);
        });
      }
    };

    // Track current visibility states
    let isTopVisible = true;
    let isEditorVisible = true;

    // Create intersection observer for the top sentinel element
    const topObserver = new IntersectionObserver(
      ([entry]) => {
        isTopVisible = entry.isIntersecting;
        updateStickyState(isTopVisible, isEditorVisible);
      },
      {
        threshold: 0,
        rootMargin: '-1px 0px 0px 0px' // Trigger 1px before the element leaves the viewport
      }
    );

    // Create intersection observer for the editor container
    const editorObserver = new IntersectionObserver(
      ([entry]) => {
        isEditorVisible = entry.isIntersecting;
        updateStickyState(isTopVisible, isEditorVisible);
      },
      {
        threshold: 0, // Even a small part of the editor being visible counts
        rootMargin: '0px'
      }
    );

    // Observe the sentinel elements
    topObserver.observe(observerRef.current);

    // Find the editor container to observe
    const editorContainer = document.getElementById('markdown-editor-container');
    if (editorContainer) {
      editorObserver.observe(editorContainer);
    }

    // Handle window resize to recalculate heights
    const handleResize = () => {
      if (toolbarRef.current) {
        setToolbarHeight(toolbarRef.current.offsetHeight);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      topObserver.disconnect();
      editorObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [isSticky]);

  // State for editing existing images
  const [editingImageData, setEditingImageData] = useState<{
    url: string;
    alt: string;
    size: 'small' | 'medium' | 'large' | 'custom';
    width?: number;
    height?: number;
    position?: number;
  } | null>(null);

  // Listen for slash command to open image dialog
  useEffect(() => {
    const handleToolbarOpenImageDialog = () => {
      setIsImageDialogOpen(true);
    };

    const handleToolbarEditImageDialog = (event: CustomEvent) => {
      console.log('TOOLBAR: Received toolbar-edit-image-dialog event');
      const { imageData, position } = event.detail;
      console.log('TOOLBAR: Event detail:', { imageData, position });

      const editData = { ...imageData, position };
      console.log('TOOLBAR: Setting editingImageData:', editData);
      setEditingImageData(editData);
      setIsImageDialogOpen(true);
      console.log('TOOLBAR: Dialog opened');
    };

    document.addEventListener('toolbar-open-image-dialog', handleToolbarOpenImageDialog);
    document.addEventListener('toolbar-edit-image-dialog', handleToolbarEditImageDialog as EventListener);

    return () => {
      document.removeEventListener('toolbar-open-image-dialog', handleToolbarOpenImageDialog);
      document.removeEventListener('toolbar-edit-image-dialog', handleToolbarEditImageDialog as EventListener);
    };
  }, []);

  // Using shared editor state utility functions from utils/editor-state.ts

  // Force component to update on editor state changes
  const [updateCounter, setUpdateCounter] = React.useState(0);

  // Add effect to listen for editor state changes
  React.useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      // Increment counter to force re-render
      setUpdateCounter(prev => prev + 1);
    };

    // Listen for both selection and transaction updates
    editor.on('selectionUpdate', handleUpdate);
    editor.on('transaction', handleUpdate);

    return () => {
      editor.off('selectionUpdate', handleUpdate);
      editor.off('transaction', handleUpdate);
    };
  }, [editor]);

  // Determine button states based on editor context
  const generateDisabled = !editor || isHeading(editor);

  const refineDisabled = !editor || (
    (isParagraph(editor) && isParagraphEmpty(editor)) || // Disabled on empty paragraphs
    (!isParagraph(editor) && !editor.state.selection.content().size) || // Disabled on non-paragraphs with no selection
    selectionContainsHeading(editor) // Disabled if selection contains headings
  );

  const summarizeDisabled = !editor || !isHeading(editor);

  // Handle image insertion or update
  const handleImageInsert = (url: string, alt: string, size: 'small' | 'medium' | 'large' | 'custom', customWidth?: number, customHeight?: number) => {
    if (!editor) return;

    console.log('UPDATE: handleImageInsert called');
    console.log('UPDATE: Parameters:', { url, alt, size, customWidth, customHeight });
    console.log('UPDATE: editingImageData:', editingImageData);

    // Define size classes
    const sizeClasses = {
      small: 'max-w-xs', // ~300px
      medium: 'max-w-2xl', // ~600px  
      large: 'max-w-4xl', // ~900px
      custom: 'max-w-none', // No max-width constraint for custom sizes
    };

    // Create new markdown with size syntax
    const sizeMap = {
      small: '300x',
      medium: '600x',
      large: '900x'
    };

    // Handle custom size
    let finalSizeMap: string;
    let finalStyle: string;

    if (size === 'custom' && customWidth && customHeight) {
      finalSizeMap = `${customWidth}x${customHeight}`;
      finalStyle = `width: ${customWidth}px; height: ${customHeight}px;`;
    } else if (size === 'custom' && customWidth) {
      finalSizeMap = `${customWidth}x`;
      finalStyle = `width: ${customWidth}px; height: auto;`;
    } else {
      finalSizeMap = sizeMap[size as keyof typeof sizeMap];
      finalStyle = `max-width: ${size === 'small' ? '300px' : size === 'medium' ? '600px' : '900px'}; height: auto;`;
    }

    if (editingImageData && editingImageData.position !== undefined) {
      // Update existing image by replacing with new markdown
      const { position } = editingImageData;
      console.log('UPDATE: Updating existing image at position:', position);

      const newMarkdown = `![${alt || 'Image'}](${url} =${finalSizeMap})`;
      console.log('UPDATE: New markdown:', newMarkdown);

      // Try to replace the image node at the specific position
      try {
        const { state, dispatch } = editor.view;
        const { tr } = state;

        // Find the image node at the position
        const node = state.doc.nodeAt(position);
        console.log('UPDATE: Node at position:', node);

        if (node && node.type.name === 'image') {
          console.log('UPDATE: Found image node, replacing with new markdown');

          // Replace the image node with new attributes that include size info
          const updateAttrs = {
            src: url,
            alt: alt,
            class: sizeClasses[size],
            style: finalStyle,
            'data-size': finalSizeMap // Store size info for markdown conversion
          };

          const transaction = tr.setNodeMarkup(position, undefined, updateAttrs);
          dispatch(transaction);
          console.log('UPDATE: Image replaced successfully');
        } else {
          console.log('UPDATE: No image node found at position, trying fallback');
          // Fallback: select the position and insert new content
          editor.chain().focus().setTextSelection(position).insertContent(newMarkdown).run();
        }

        // Re-focus the editor to ensure onBlur fires correctly later
        setTimeout(() => {
          editor.chain().focus().run();
        })
      } catch (error) {
        console.error('UPDATE: Error updating image:', error);
        // Fallback: just insert at current cursor position
        editor.chain().focus().insertContent(newMarkdown).run();
      }
    } else {
      // Insert new image with proper attributes
      console.log('Inserting new image');

      const imageAttrs = {
        src: url,
        alt: alt,
        class: sizeClasses[size],
        style: finalStyle,
        'data-size': finalSizeMap // Store size info for markdown conversion
      };
      console.log('Image attributes:', imageAttrs);

      editor.chain().focus().insertContent({
        type: 'image',
        attrs: imageAttrs,
      }).run();
    }
  };

  const handleCloseImageDialog = () => {
    setIsImageDialogOpen(false);
    setEditingImageData(null);
  };

  // Link handlers
  const handleOpenLinkDialog = () => {
    if (!editor) return;

    // Check if cursor is on a link
    const { state } = editor;
    const { from, to, $from } = state.selection;
    const link = editor.getAttributes('link');

    if (link.href) {
      // Editing existing link - need to get the full link text
      // Use TipTap's built-in method to get the mark range
      // First, extend the selection to cover the full link
      const linkMarkType = state.schema.marks.link;

      // Find the link mark range using ProseMirror's mark resolution
      let linkStart = from;
      let linkEnd = to;

      // Resolve position and check marks at different offsets
      // When cursor is at edge, we need to check both before and after
      const $pos = $from;

      // Get the text node range that contains the link
      // Start by finding any position that has the link mark
      let foundLinkAt = -1;

      // Check current position and nearby positions for the link mark
      for (let offset = 0; offset <= 1; offset++) {
        const checkPos = Math.min(from + offset, state.doc.content.size);
        if (checkPos > 0) {
          const resolved = state.doc.resolve(checkPos);
          const marks = resolved.marks();
          if (marks.some(m => m.type.name === 'link' && m.attrs.href === link.href)) {
            foundLinkAt = checkPos;
            break;
          }
        }
      }

      // If we couldn't find it forward, try backward
      if (foundLinkAt === -1 && from > 0) {
        const resolved = state.doc.resolve(from - 1);
        const marks = resolved.marks();
        if (marks.some(m => m.type.name === 'link' && m.attrs.href === link.href)) {
          foundLinkAt = from - 1;
        }
      }

      if (foundLinkAt >= 0) {
        // Now scan from the found position to get full link range
        // Scan backwards to find link start
        for (let i = foundLinkAt; i >= 0; i--) {
          const pos = state.doc.resolve(i);
          const marks = pos.marks();
          const hasLinkMark = marks.some(m => m.type.name === 'link' && m.attrs.href === link.href);
          if (!hasLinkMark) {
            linkStart = i;
            break;
          }
          if (i === 0) {
            linkStart = 0;
            break;
          }
        }

        // Scan forwards to find link end
        for (let i = foundLinkAt; i <= state.doc.content.size; i++) {
          const pos = state.doc.resolve(i);
          const marks = pos.marks();
          const hasLinkMark = marks.some(m => m.type.name === 'link' && m.attrs.href === link.href);
          if (!hasLinkMark) {
            linkEnd = i - 1;
            break;
          }
          if (i === state.doc.content.size) {
            linkEnd = i;
            break;
          }
        }

        const linkText = state.doc.textBetween(linkStart, linkEnd);

        console.log('[LinkDialog] Editing link:', link.href, 'Text:', linkText, 'Range:', linkStart, '-', linkEnd);
        setLinkData({ url: link.href, text: linkText, isEditing: true });
      } else {
        // Fallback: we detected a link but couldn't find the mark range
        console.log('[LinkDialog] Link detected but mark range not found, using href only');
        setLinkData({ url: link.href, text: '', isEditing: true });
      }
    } else {
      // Creating new link
      let selectedText = state.doc.textBetween(from, to);

      // Trim trailing/leading spaces from selection
      // Users often accidentally select extra spaces
      selectedText = selectedText.trim();

      setLinkData({ url: '', text: selectedText, isEditing: false });
    }
    setIsLinkDialogOpen(true);
  };

  // Listen for link dialog event from slash command
  useEffect(() => {
    const handleLinkDialogEvent = () => {
      handleOpenLinkDialog();
    };

    document.addEventListener('open-link-dialog', handleLinkDialogEvent);
    return () => {
      document.removeEventListener('open-link-dialog', handleLinkDialogEvent);
    };
  }, [editor]);

  const handleSaveLink = (url: string, text?: string) => {
    if (!editor) return;

    if (linkData.isEditing) {
      // Update existing link
      if (text && text !== linkData.text) {
        // If text changed, replace the entire link
        editor.chain()
          .focus()
          .extendMarkRange('link')
          .deleteSelection()
          .insertContent(`<a href="${url}">${text}</a>`)
          .run();
      } else {
        // Just update the URL
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
      }
    } else {
      // Insert new link
      const { state } = editor;
      let { from, to } = state.selection;
      const hasSelection = from !== to;

      if (hasSelection) {
        // Get the selected text and find actual content boundaries (trim spaces)
        const selectedText = state.doc.textBetween(from, to);
        const trimmedText = selectedText.trim();

        if (trimmedText.length > 0 && trimmedText.length < selectedText.length) {
          // Calculate how many spaces to skip
          const leadingSpaces = selectedText.indexOf(trimmedText);
          const trailingSpaces = selectedText.length - trimmedText.length - leadingSpaces;

          // Adjust selection to exclude leading/trailing spaces
          from = from + leadingSpaces;
          to = to - trailingSpaces;

          // Set the adjusted selection and apply link
          editor.chain()
            .focus()
            .setTextSelection({ from, to })
            .setLink({ href: url })
            .run();
        } else {
          // No trimming needed, wrap selected text with link
          editor.chain().focus().setLink({ href: url }).run();
        }
      } else if (text) {
        // Insert link with custom text
        editor.chain().focus().insertContent(`<a href="${url}">${text}</a>`).run();
      } else {
        // Insert link with URL as text
        editor.chain().focus().insertContent(`<a href="${url}">${url}</a>`).run();
      }
    }
  };

  const handleRemoveLink = () => {
    if (!editor) return;
    editor.chain().focus().unsetLink().run();
  };

  // Log current state for debugging
  if (editor) {
    const currentNodeType = editor.isActive('heading')
      ? `heading-${editor.isActive('heading', { level: 1 }) ? '1' :
        editor.isActive('heading', { level: 2 }) ? '2' :
          editor.isActive('heading', { level: 3 }) ? '3' : 'other'}`
      : editor.isActive('paragraph')
        ? 'paragraph'
        : 'other';
    // console.log('Current node type:', currentNodeType, 'Update counter:', updateCounter);
  }
  return (
    <>
      {/* Observer element to detect when toolbar should become sticky */}
      <div ref={observerRef} className="absolute top-0 left-0 h-1 w-full opacity-0 pointer-events-none" />

      {/* Placeholder div to prevent content jump when toolbar becomes fixed */}
      {isSticky && <div style={{ height: toolbarHeight }} />}

      {/* Actual toolbar that can be fixed or sticky */}
      <div
        ref={toolbarRef}
        className={`${isSticky ? 'fixed' : 'relative'} top-0 z-50 bg-white border-b border-gray-200 shadow-sm will-change-transform editor-toolbar`}
        style={{
          transition: 'transform 150ms ease-in-out',
          width: isSticky ? 'var(--editor-width, 100%)' : '100%',
          left: isSticky ? 'var(--editor-left, 0)' : '0',
          maxWidth: isSticky ? 'var(--editor-width, 100%)' : '100%'
        }}>
        <div className="flex items-center justify-between px-4 py-2">
          {isAiProcessing || isUploadingImage || isReadOnly ? (
            // Disabled Mode (AI Processing / Uploading / Title Editing)
            <div className="flex items-center space-x-3 flex-1">
              {!isReadOnly && <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 flex-shrink-0"></div>}
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900">
                  {isReadOnly ? 'Editor is disabled while editing title' : isAiProcessing ? 'AI is processing your request...' : 'Uploading image...'}
                </p>
                <p className="text-xs text-blue-700">
                  {isReadOnly ? 'Please save or cancel title editing to re-enable the editor.' : 'Editor is in read-only mode. You can scroll and review content while waiting.'}
                </p>
              </div>
            </div>
          ) : (
            // Normal Toolbar Mode
            <>
              <div className="flex items-center space-x-1 flex-wrap flex-grow mr-4">
                {/* AI Tools */}
                <ToolbarButton
                  icon={<Sparkles className="h-4 w-4 text-purple-500" />}
                  isActive={false}
                  onClick={() => {
                    onAICommand('generate-image');
                  }}
                  disabled={viewMode === 'markdown'}
                  tooltip={viewMode === 'markdown' ? "Switch to Visual mode to use AI" : "Find / Generate Image with AI"}
                />

                <div className="h-4 w-px bg-gray-200 mx-2" />

                {/* History - only show when not in agent mode to avoid conflicts */}
                {!isAgentMode && (
                  <>
                    <ToolbarButton
                      icon={<Undo className="h-4 w-4" />}
                      isActive={false}
                      onClick={() => editor?.chain().focus().undo().run()}
                      disabled={!editor || !editor.can().undo()}
                      tooltip="Undo"
                    />
                    <ToolbarButton
                      icon={<Redo className="h-4 w-4" />}
                      isActive={false}
                      onClick={() => editor?.chain().focus().redo().run()}
                      disabled={!editor || !editor.can().redo()}
                      tooltip="Redo"
                    />
                    <div className="h-4 w-px bg-gray-200 mx-2" />
                  </>
                )}

                {/* Node Selector - wider with text */}
                <NodeSelector editor={editor} open={isNodeSelectorOpen} onOpenChange={setIsNodeSelectorOpen} />

                <div className="h-4 w-px bg-gray-200 mx-2" />

                {/* Text Formatting Tools */}
                <ToolbarButton
                  icon={<Bold className="h-4 w-4" />}
                  isActive={editor?.isActive('bold')}
                  onClick={() => editor?.chain().focus().toggleBold().run()}
                  disabled={!editor}
                  tooltip="Bold"
                />
                <ToolbarButton
                  icon={<Italic className="h-4 w-4" />}
                  isActive={editor?.isActive('italic')}
                  onClick={() => editor?.chain().focus().toggleItalic().run()}
                  disabled={!editor}
                  tooltip="Italic"
                />
                <ToolbarButton
                  icon={<Underline className="h-4 w-4" />}
                  isActive={editor?.isActive('underline')}
                  onClick={() => editor?.chain().focus().toggleUnderline().run()}
                  disabled={!editor}
                  tooltip="Underline"
                />
                <ToolbarButton
                  icon={<Strikethrough className="h-4 w-4" />}
                  isActive={editor?.isActive('strike')}
                  onClick={() => editor?.chain().focus().toggleStrike().run()}
                  disabled={!editor}
                  tooltip="Strikethrough"
                />
                <ToolbarButton
                  icon={<Code className="h-4 w-4" />}
                  isActive={editor?.isActive('code')}
                  onClick={() => editor?.chain().focus().toggleCode().run()}
                  disabled={!editor}
                  tooltip="Inline Code"
                />
                <ToolbarButton
                  icon={<LinkIcon className="h-4 w-4" />}
                  isActive={editor?.isActive('link')}
                  onClick={handleOpenLinkDialog}
                  disabled={!editor}
                  tooltip="Insert/Edit Link"
                />

                <div className="h-4 w-px bg-gray-200 mx-2" />

                {/* Lists & Block Formatting */}
                <ToolbarButton
                  icon={<List className="h-4 w-4" />}
                  isActive={editor?.isActive('bulletList')}
                  onClick={() => editor?.chain().focus().toggleBulletList().run()}
                  disabled={!editor}
                  tooltip="Bullet List"
                />
                <ToolbarButton
                  icon={<ListOrdered className="h-4 w-4" />}
                  isActive={editor?.isActive('orderedList')}
                  onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                  disabled={!editor}
                  tooltip="Numbered List"
                />
                <ToolbarButton
                  icon={<CheckSquare className="h-4 w-4" />}
                  isActive={editor?.isActive('taskList')}
                  onClick={() => editor?.chain().focus().toggleTaskList().run()}
                  disabled={!editor}
                  tooltip="Task List"
                />
                <ToolbarButton
                  icon={<TableCellsIcon className="h-4 w-4" />}
                  isActive={editor?.isActive('table')}
                  onClick={() => editor?.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                  disabled={!editor}
                  tooltip="Insert Table"
                />
                <ToolbarButton
                  icon={<Quote className="h-4 w-4" />}
                  isActive={editor?.isActive('blockquote')}
                  onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                  disabled={!editor}
                  tooltip="Blockquote"
                />
                <ToolbarButton
                  icon={<FileCode className="h-4 w-4" />}
                  isActive={editor?.isActive('codeBlock')}
                  onClick={() => editor?.chain().focus().toggleCodeBlock().run()}
                  disabled={!editor}
                  tooltip="Code Block"
                />

                <div className="h-4 w-px bg-gray-200 mx-2" />

                {/* Image Insertion */}
                <ToolbarButton
                  icon={<PhotoIcon className="h-4 w-4" />}
                  isActive={false}
                  onClick={() => setIsImageDialogOpen(true)}
                  disabled={!editor || viewMode === 'markdown'}
                  tooltip={viewMode === 'markdown' ? "Switch to Visual mode to insert images" : "Insert Image"}
                />
              </div>

              {/* Right side of toolbar with status */}

              {/* Agent Mode Toggle */}
              {!isAgentMode && (
                <ToolbarButton
                  icon={<Bot className="h-4 w-4" />}
                  isActive={isAgentMode}
                  onClick={onToggleAgentMode || (() => { })}
                  disabled={!onToggleAgentMode}
                  tooltip={'Enter Agent Mode'}
                />)}

              <div className="h-4 w-px bg-gray-200 mx-2" />

              {/* View Mode Switchers */}
              <div className="flex bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                <Tooltip
                  title="Visual Editor (WYSIWYG)"
                  position="bottom"
                  animation="fade"
                  arrow={true}
                  delay={100}
                  distance={10}
                  theme="dark"
                  hideOnClick={false}
                  checked={false}   // Workaround for unknown props if any
                  style={{ display: 'flex' }} // Critical to keep button layout correct inside flex container
                >
                  <button
                    type="button"
                    onClick={() => onViewModeChange('visual')}
                    className={`p-1.5 rounded-md text-xs font-medium flex items-center gap-1 transition-all ${viewMode === 'visual'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                      }`}
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </Tooltip>
                <Tooltip
                  title="Markdown Mode"
                  position="bottom"
                  animation="fade"
                  arrow={true}
                  delay={100}
                  distance={10}
                  theme="dark"
                  hideOnClick={false}
                  style={{ display: 'flex' }} // Critical to keep button layout correct inside flex container
                >
                  <button
                    type="button"
                    onClick={() => onViewModeChange('markdown')}
                    className={`p-1.5 rounded-md text-xs font-medium flex items-center gap-1 transition-all ${viewMode === 'markdown'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200/50'
                      }`}
                  >
                    <Code className="w-4 h-4" />
                  </button>
                </Tooltip>
              </div>

              {!isAgentMode && (<>
                <div className="h-4 w-px bg-gray-200 mx-2" />

                <div className="flex items-center space-x-2 flex-shrink-0">
                  {/* Save button or status indicators */}
                  <div className="flex items-center gap-2">
                    {wordCount !== null && (
                      <div className="rounded-lg bg-stone-100 px-2 py-1 text-sm text-stone-600">
                        {wordCount} words
                      </div>
                    )}

                    {hasUnsavedChanges && onSave ? (
                      <button
                        onClick={onSave}
                        className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 transition-all duration-200 border border-blue-600"
                        style={{ backgroundColor: '#2563eb', color: '#ffffff' }}
                      >
                        Save Changes
                      </button>
                    ) : (
                      <div className="rounded-lg bg-stone-100 px-2 py-1 text-sm text-stone-600">
                        {saveStatus}
                      </div>
                    )}
                  </div>
                </div>
              </>
              )}
            </>
          )}
        </div>
        {/* Animated progress bar at bottom - only show during AI processing */}
        {(isAiProcessing || isUploadingImage) && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-indigo-500 to-pink-500 animate-shimmer"
            style={{ backgroundSize: '200% 100%' }}></div>
        )}
      </div>

      {/* Image Insertion Dialog */}
      <ImageInsertionDialog
        isOpen={isImageDialogOpen}
        onClose={handleCloseImageDialog}
        onInsert={handleImageInsert}
        initialData={editingImageData}
        mode={editingImageData ? 'edit' : 'insert'}
      />

      {/* Link Dialog */}
      <LinkDialog
        isOpen={isLinkDialogOpen}
        onClose={() => setIsLinkDialogOpen(false)}
        onSave={handleSaveLink}
        onRemove={handleRemoveLink}
        initialUrl={linkData.url}
        initialText={linkData.text}
        isEditing={linkData.isEditing}
      />
    </>
  );
};
